import { useCallback, useEffect, useState } from "react";
import { DashboardSaleSummary } from "../../types/dashboard.type";
import { getDashboardSaleSummary } from "../../services";
import { formatNumber } from "../../utils";
import { Dayjs } from "dayjs";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

import ProfitAndCostSkeleton from "./Skeleton/ProfitAndCostSkeleton";

const compact = "@media " + COMPACT_QUERY;

interface ProfitAndCostProps {
  selectedDate: Dayjs | null;
}

export default function ProfitAndCost({ selectedDate }: ProfitAndCostProps) {
  const [saleSummary, setSaleSummary] = useState<DashboardSaleSummary>();

  const onGetSaleSummary = useCallback(async (date: Dayjs) => {
    const {
      data: { data },
    } = await getDashboardSaleSummary(date.format("YYYY-MM-DD"));
    setSaleSummary(data);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      onGetSaleSummary(selectedDate);
    }
  }, [selectedDate, onGetSaleSummary]);

  if (!saleSummary) return <ProfitAndCostSkeleton />;

  return (
    <Paper
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography
        variant="body1"
        fontWeight={600}
        mb={2}
        sx={{ [compact]: { mb: 1 } }}
      >
        ข้อมูลต้นทุน/กำไรขั้นต้นของวันนี้
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: (theme) => theme.palette.primary.main + "1A",
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: 3,
            p: 1.5,
            [compact]: { p: 1.25 },
          }}
        >
            <CurrencyExchangeOutlinedIcon
              color="primary"
              sx={{ fontSize: 28, mb: 0.5, [compact]: { mb: 0.25 } }}
            />
            <Typography color="text.secondary" variant="body1">
              ต้นทุนสินค้า
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary" mb={0.25}>
              ฿{formatNumber(saleSummary.total_product_cost)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {saleSummary.total_product_cost_percent
                ? `(${saleSummary.total_product_cost_percent}%)`
                : ""}
            </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: (theme) => theme.palette.success.main + "1A",
            border: (theme) => `2px solid ${theme.palette.success.main}`,
            borderRadius: 3,
            p: 1.5,
            [compact]: { p: 1.25 },
          }}
        >
            <PaymentsOutlinedIcon
              color="success"
              sx={{ fontSize: 28, mb: 0.5, [compact]: { mb: 0.25 } }}
            />
            <Typography color="text.secondary" variant="body1">
              กำไรขั้นต้น
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success" mb={0.25}>
              ฿{formatNumber(saleSummary.total_revenue)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {saleSummary.total_revenue_percent
                ? `(${saleSummary.total_revenue_percent}%)`
                : ""}
            </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
